import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  applyCardLabelBodySchema,
  cardDetailSchema,
  createLabelBodySchema,
  labelColourPatchSchema,
  labelListSchema,
  labelNamePatchSchema,
  labelSchema,
  type ApiError,
  type ApplyCardLabelBody,
  type BoardContents,
  type CardDetail,
  type CreateLabelBody,
  type Label,
  type LabelColourPatch,
  type LabelNamePatch,
} from '@molt/shared';
import { applyCardDetailToBoardContents } from './cards';
import { announce } from './announce';
import { del, emptyResponseSchema, get, patch, post } from './api';
import { useInterfaceLanguage } from './interface-language';
import { queryKeys } from './query-keys';

export function getBoardLabels(boardId: string): Promise<Label[]> {
  return get(`/boards/${encodeURIComponent(boardId)}/labels`, labelListSchema);
}

export function createLabel(boardId: string, body: CreateLabelBody): Promise<Label> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/labels`,
    labelSchema,
    createLabelBodySchema.parse(body),
  );
}

export function renameLabel(labelId: string, body: LabelNamePatch): Promise<Label> {
  return patch(
    `/labels/${encodeURIComponent(labelId)}/name`,
    labelSchema,
    labelNamePatchSchema.parse(body),
  );
}

export function recolourLabel(labelId: string, body: LabelColourPatch): Promise<Label> {
  return patch(
    `/labels/${encodeURIComponent(labelId)}/colour`,
    labelSchema,
    labelColourPatchSchema.parse(body),
  );
}

export function deleteLabel(labelId: string): Promise<null> {
  return del(`/labels/${encodeURIComponent(labelId)}`, emptyResponseSchema);
}

export function applyCardLabel(
  cardId: string,
  body: ApplyCardLabelBody,
): Promise<CardDetail> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/labels`,
    cardDetailSchema,
    applyCardLabelBodySchema.parse(body),
  );
}

export function removeCardLabel(cardId: string, labelId: string): Promise<CardDetail> {
  return del(
    `/cards/${encodeURIComponent(cardId)}/labels/${encodeURIComponent(labelId)}`,
    cardDetailSchema,
  );
}

export function boardLabelsQueryOptions(
  boardId: string,
): UseQueryOptions<Label[], ApiError> {
  return {
    queryKey: queryKeys.boards.labels(boardId),
    queryFn: () => getBoardLabels(boardId),
  };
}

export interface ToggleCardLabelVariables {
  readonly cardId: string;
  readonly boardId: string;
  readonly labelId: string;
}

export interface CreateLabelVariables {
  readonly boardId: string;
  readonly body: CreateLabelBody;
}

export interface RenameLabelVariables {
  readonly boardId: string;
  readonly labelId: string;
  readonly name: LabelNamePatch['name'];
}

export interface RecolourLabelVariables {
  readonly boardId: string;
  readonly labelId: string;
  readonly colour: LabelColourPatch['colour'];
}

export interface DeleteLabelVariables {
  readonly boardId: string;
  readonly labelId: string;
}

export interface LabelCachePatch {
  readonly name?: Label['name'];
  readonly colour?: Label['colour'];
}

function writeCardDetail(
  queryClient: ReturnType<typeof useQueryClient>,
  detail: CardDetail,
  boardId: string,
) {
  queryClient.setQueryData(queryKeys.cards.detail(detail.id), detail);
  queryClient.setQueryData(queryKeys.boards.contents(boardId), (previous) =>
    applyCardDetailToBoardContents(previous as BoardContents | undefined, detail),
  );
}

function mergeLabelForCache(
  existing: Label | undefined,
  label: Label,
  patch: LabelCachePatch,
): Label {
  return {
    ...existing,
    ...label,
    name: 'name' in patch ? (patch.name ?? null) : (existing?.name ?? label.name),
    colour:
      'colour' in patch
        ? (patch.colour ?? label.colour)
        : (existing?.colour ?? label.colour),
  };
}

function mergeCardLabelForCache(
  existing: CardDetail['labels'][number],
  label: Label,
  patch: LabelCachePatch,
): CardDetail['labels'][number] {
  return {
    id: label.id,
    name: 'name' in patch ? (patch.name ?? null) : (existing.name ?? label.name),
    colour:
      'colour' in patch
        ? (patch.colour ?? label.colour)
        : (existing.colour ?? label.colour),
  };
}

export function replaceBoardLabel(
  previous: BoardContents | undefined,
  label: Label,
  patch: LabelCachePatch = {},
): BoardContents | undefined {
  if (previous === undefined) return undefined;
  return {
    ...previous,
    labels: previous.labels.map((item) =>
      item.id === label.id ? mergeLabelForCache(item, label, patch) : item,
    ),
    lists: previous.lists.map((list) => ({
      ...list,
      cards: list.cards.map((card) => ({
        ...card,
        labels: card.labels.map((item) =>
          item.id === label.id ? mergeCardLabelForCache(item, label, patch) : item,
        ),
      })),
    })),
  };
}

export function removeBoardLabel(
  previous: BoardContents | undefined,
  labelId: string,
): BoardContents | undefined {
  if (previous === undefined) return undefined;
  return {
    ...previous,
    labels: previous.labels.filter((label) => label.id !== labelId),
    lists: previous.lists.map((list) => ({
      ...list,
      cards: list.cards.map((card) => ({
        ...card,
        labels: card.labels.filter((label) => label.id !== labelId),
      })),
    })),
  };
}

function isCardDetailCacheEntry(value: unknown): value is CardDetail {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { readonly labels?: unknown }).labels)
  );
}

export function replaceCachedCardLabel(
  previous: unknown,
  label: Label,
  patch: LabelCachePatch = {},
): unknown {
  if (!isCardDetailCacheEntry(previous)) return previous;
  return {
    ...previous,
    labels: previous.labels.map((item) =>
      item.id === label.id ? mergeCardLabelForCache(item, label, patch) : item,
    ),
  };
}

export function removeCachedCardLabel(previous: unknown, labelId: string): unknown {
  if (!isCardDetailCacheEntry(previous)) return previous;
  return {
    ...previous,
    labels: previous.labels.filter((label) => label.id !== labelId),
  };
}

export function useApplyCardLabel(): UseMutationResult<
  CardDetail,
  unknown,
  ToggleCardLabelVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      applyCardLabel(variables.cardId, { labelId: variables.labelId }),
    onSuccess: (detail, variables) =>
      writeCardDetail(queryClient, detail, variables.boardId),
    onError: () => announce(t('primitives.labels.applyFailed')),
  });
}

export function useRemoveCardLabel(): UseMutationResult<
  CardDetail,
  unknown,
  ToggleCardLabelVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) => removeCardLabel(variables.cardId, variables.labelId),
    onSuccess: (detail, variables) =>
      writeCardDetail(queryClient, detail, variables.boardId),
    onError: () => announce(t('primitives.labels.removeFailed')),
  });
}

export function useCreateLabel(): UseMutationResult<
  Label,
  unknown,
  CreateLabelVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) => createLabel(variables.boardId, variables.body),
    onSuccess: (label, variables) => {
      queryClient.setQueryData(queryKeys.boards.labels(variables.boardId), (previous) =>
        previous === undefined ? [label] : [...(previous as Label[]), label],
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.boardId),
      });
    },
    onError: () => announce(t('primitives.labels.createFailed')),
  });
}

export function useRenameLabel(): UseMutationResult<
  Label,
  unknown,
  RenameLabelVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) => renameLabel(variables.labelId, { name: variables.name }),
    onSuccess: (label, variables) => {
      const patch = { name: variables.name };
      queryClient.setQueryData(queryKeys.boards.labels(variables.boardId), (previous) =>
        previous === undefined
          ? previous
          : (previous as Label[]).map((item) =>
              item.id === label.id ? mergeLabelForCache(item, label, patch) : item,
            ),
      );
      queryClient.setQueryData(queryKeys.boards.contents(variables.boardId), (previous) =>
        replaceBoardLabel(previous as BoardContents | undefined, label, patch),
      );
      queryClient.setQueriesData<unknown>(
        { queryKey: queryKeys.cards.all() },
        (previous: unknown) => replaceCachedCardLabel(previous, label, patch),
      );
    },
    onError: () => announce(t('primitives.labels.renameFailed')),
  });
}

export function useRecolourLabel(): UseMutationResult<
  Label,
  unknown,
  RecolourLabelVariables
> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) =>
      recolourLabel(variables.labelId, { colour: variables.colour }),
    onSuccess: (label, variables) => {
      const patch = { colour: variables.colour };
      queryClient.setQueryData(queryKeys.boards.labels(variables.boardId), (previous) =>
        previous === undefined
          ? previous
          : (previous as Label[]).map((item) =>
              item.id === label.id ? mergeLabelForCache(item, label, patch) : item,
            ),
      );
      queryClient.setQueryData(queryKeys.boards.contents(variables.boardId), (previous) =>
        replaceBoardLabel(previous as BoardContents | undefined, label, patch),
      );
      queryClient.setQueriesData<unknown>(
        { queryKey: queryKeys.cards.all() },
        (previous: unknown) => replaceCachedCardLabel(previous, label, patch),
      );
    },
    onError: () => announce(t('primitives.labels.recolourFailed')),
  });
}

export function useDeleteLabel(): UseMutationResult<null, unknown, DeleteLabelVariables> {
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  return useMutation({
    mutationFn: (variables) => deleteLabel(variables.labelId),
    onSuccess: (_result, variables) => {
      queryClient.setQueryData(queryKeys.boards.labels(variables.boardId), (previous) =>
        previous === undefined
          ? previous
          : (previous as Label[]).filter((item) => item.id !== variables.labelId),
      );
      queryClient.setQueriesData<unknown>(
        { queryKey: queryKeys.cards.all() },
        (previous: unknown) => removeCachedCardLabel(previous, variables.labelId),
      );
      queryClient.setQueryData(queryKeys.boards.contents(variables.boardId), (previous) =>
        removeBoardLabel(previous as BoardContents | undefined, variables.labelId),
      );
    },
    onError: () => announce(t('primitives.labels.deleteFailed')),
  });
}
